import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../utils/api';

interface Machine {
  id: string;
  code: string;
  location: string;
  city?: string;
  status: string;
  latitude?: number;
  longitude?: number;
  distance?: number;
}

export const RoutesComponent: React.FC = () => {
  const [userLat, setUserLat] = useState<string>('');
  const [userLng, setUserLng] = useState<string>('');
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [sortedMachines, setSortedMachines] = useState<Machine[]>([]);
  const [isCalculated, setIsCalculated] = useState(false);

  const { data: machines, isLoading } = useQuery({
    queryKey: ['admin-machines'],
    queryFn: async () => {
      const response = await api.get('/admin/machines');
      return Array.isArray(response.data) ? response.data as Machine[] : [];
    },
  });

  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    // Haversine formula to calculate distance between two coordinates
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const handleCalculateRoute = () => {
    const lat = parseFloat(userLat);
    const lng = parseFloat(userLng);

    if (isNaN(lat) || isNaN(lng)) {
      alert('Por favor, insira coordenadas válidas');
      return;
    }

    if (!machines) return;

    // Filter machines with coordinates and calculate distances
    const machinesWithDistance = machines
      .filter(m => m.latitude && m.longitude)
      .map(machine => ({
        ...machine,
        distance: calculateDistance(lat, lng, machine.latitude!, machine.longitude!)
      }))
      .sort((a, b) => a.distance! - b.distance!);

    setSortedMachines(machinesWithDistance);
    setIsCalculated(true);
    setShowLocationModal(false);
  };

  const handleGetCurrentLocation = () => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLat(position.coords.latitude.toFixed(6));
          setUserLng(position.coords.longitude.toFixed(6));
        },
        (error) => {
          alert('Erro ao obter localização: ' + error.message);
        }
      );
    } else {
      alert('Geolocalização não é suportada pelo seu navegador');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online':
        return 'bg-green-100 text-green-800';
      case 'offline':
        return 'bg-red-100 text-red-800';
      case 'maintenance':
        return 'bg-yellow-100 text-yellow-800';
      case 'in_use':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'online':
        return 'Disponível';
      case 'offline':
        return 'Desligada';
      case 'maintenance':
        return 'Em Manutenção';
      case 'in_use':
        return 'Em Uso';
      default:
        return status;
    }
  };

  const openInGoogleMaps = (machine: Machine) => {
    if (machine.latitude && machine.longitude) {
      const url = `https://www.google.com/maps/dir/?api=1&origin=${userLat},${userLng}&destination=${machine.latitude},${machine.longitude}`;
      window.open(url, '_blank');
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  const machinesWithCoordinates = machines?.filter(m => m.latitude && m.longitude).length || 0;
  const machinesWithoutCoordinates = (machines?.length || 0) - machinesWithCoordinates;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Planejamento de Rotas</h2>
          <p className="text-sm text-gray-600 mt-1">
            Organize suas visitas às máquinas por proximidade
          </p>
        </div>
        <button
          onClick={() => setShowLocationModal(true)}
          className="bg-orange-600 text-white px-6 py-3 rounded-lg hover:bg-orange-700 font-medium shadow-lg transition-all flex items-center justify-center"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Calcular Rota
        </button>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-orange-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total de Máquinas</p>
              <p className="text-2xl font-bold text-gray-900">{machines?.length || 0}</p>
            </div>
            <svg className="w-10 h-10 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
            </svg>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Com Coordenadas</p>
              <p className="text-2xl font-bold text-gray-900">{machinesWithCoordinates}</p>
            </div>
            <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-red-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Sem Coordenadas</p>
              <p className="text-2xl font-bold text-gray-900">{machinesWithoutCoordinates}</p>
            </div>
            <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
        </div>
      </div>

      {/* Warning if machines without coordinates */}
      {machinesWithoutCoordinates > 0 && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                <strong>Atenção:</strong> {machinesWithoutCoordinates} máquina(s) não possui(em) coordenadas cadastradas. 
                Adicione latitude e longitude no cadastro das máquinas para incluí-las no planejamento de rotas.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Route List */}
      {isCalculated && sortedMachines.length > 0 ? (
        <div className="bg-white shadow-lg rounded-xl border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-bold text-gray-900">Rota Otimizada</h3>
            <p className="text-sm text-gray-600">Máquinas ordenadas por proximidade</p>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {sortedMachines.map((machine, index) => (
                <div key={machine.id} className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex-shrink-0 w-12 h-12 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                    {index + 1}
                  </div>
                  <div className="ml-4 flex-1">
                    <div className="flex items-center space-x-3">
                      <h4 className="text-lg font-bold text-gray-900">{machine.code}</h4>
                      <span className={`text-xs font-medium rounded-full px-2 py-1 ${getStatusColor(machine.status)}`}>
                        {getStatusLabel(machine.status)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">{machine.location}</p>
                    {machine.city && <p className="text-xs text-gray-500">{machine.city}</p>}
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <p className="text-2xl font-bold text-orange-600">{machine.distance?.toFixed(2)} km</p>
                      <p className="text-xs text-gray-500">distância</p>
                    </div>
                    <button
                      onClick={() => openInGoogleMaps(machine)}
                      className="p-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      title="Abrir no Google Maps"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg p-12 text-center">
          <svg className="w-24 h-24 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
          </svg>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Nenhuma rota calculada</h3>
          <p className="text-gray-600 mb-6">
            Clique em "Calcular Rota" para ordenar as máquinas por proximidade
          </p>
        </div>
      )}

      {/* Location Modal */}
      {showLocationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Sua Localização</h3>
            <p className="text-sm text-gray-600 mb-4">
              Insira suas coordenadas ou use sua localização atual para calcular a rota otimizada
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Latitude
                </label>
                <input
                  type="text"
                  value={userLat}
                  onChange={(e) => setUserLat(e.target.value)}
                  placeholder="Ex: -23.550520"
                  className="block w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Longitude
                </label>
                <input
                  type="text"
                  value={userLng}
                  onChange={(e) => setUserLng(e.target.value)}
                  placeholder="Ex: -46.633308"
                  className="block w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>

              <button
                onClick={handleGetCurrentLocation}
                className="w-full py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center justify-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Usar Minha Localização Atual
              </button>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowLocationModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={handleCalculateRoute}
                className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium shadow-lg"
              >
                Calcular Rota
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
